/** createApi — Faz 1.1 jenerik API factory'si Amaç. */
import type {AxiosProgressEvent, AxiosResponse} from 'axios'
import {config} from '@/config'
import {getStoredToken} from '@shared/helpers/tokenizer.helper'
import {
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut,
} from '@shared/http-client'

/** Sessiz auth header üretir. - Token varsa `{ Authorization. */
function buildAuthHeader(): Record<string, string> {
  try {
    const token = getStoredToken()
    if (token?.accessToken) {
      return {Authorization: `Bearer ${token.accessToken}`}
    }
  } catch {
    // authStore mevcut değilse (test, top-level call vs.) sessizce geç
  }
  return {}
}

export interface ApiRequestOpts {
  /** Ekstra header (örn. Content-Type override). Authorization'a dokunma. */
  headers?: Record<string, string>
  /** http-client global toaster sustur. Default: true (göster). */
  showErrorOnToaster?: boolean
  /** Sadece `upload()`: giden byte'lar, giderken. */
  onUploadProgress?: (event: AxiosProgressEvent) => void
}

export interface ApiClient {
  /** POST — proje genelinde varsayılan kanal (ms-api 41/41 controller POST). */
  post: <Req, Res>(path: string, data?: Req, opts?: ApiRequestOpts) => Promise<Res>

  /** GET — public-page gibi az sayıda endpoint için. */
  get: <Res>(path?: string, opts?: ApiRequestOpts) => Promise<Res>

  /** PUT — şu anda kullanılmıyor; ileride RESTful'a geçişte hazır. */
  put: <Req, Res>(path: string, data?: Req, opts?: ApiRequestOpts) => Promise<AxiosResponse<Res>>

  /** PATCH — şu anda kullanılmıyor. */
  patch: <Req, Res>(path: string, data?: Req, opts?: ApiRequestOpts) => Promise<AxiosResponse<Res>>

  /** DELETE — şu anda kullanılmıyor. */
  delete: <Req, Res>(path: string, data?: Req, opts?: ApiRequestOpts) => Promise<AxiosResponse<Res>>

  /** multipart/form-data POST — image/screen upload kullanır. */
  upload: <Res>(path: string, formData: FormData, opts?: ApiRequestOpts) => Promise<Res>

  /** Yarı-katı: tam URL üret (debug, log, websocket subscribe için). */
  url: (path?: string) => string
}

export interface CreateApiOpts {
  /** Hangi config anahtarı root URL olarak kullanılacak. Default: 'APP_API_URL'. */
  rootKey?: string
}

/** Resource bazlı bir API client üretir. const api = createApi('feature'). */
export function createApi(resource: string, opts: CreateApiOpts = {}): ApiClient {
  const root = config(opts.rootKey ?? 'APP_API_URL')
  const cleanResource = resource.replace(/^\/|\/$/g, '')
  const baseUrl = `${root}/${cleanResource}`

  const url = (path = ''): string => {
    if (!path) return baseUrl
    return `${baseUrl}/${path.replace(/^\//, '')}`
  }

  const mergeHeaders = (extra?: Record<string, string>): Record<string, string> => ({
    ...buildAuthHeader(),
    ...(extra ?? {}),
  })

  return {
    url,

    post: <Req, Res>(path: string, data?: Req, opts?: ApiRequestOpts): Promise<Res> =>
      httpPost<Req, Res>({
        url: url(path),
        data,
        headers: mergeHeaders(opts?.headers),
        showErrorOnToaster: opts?.showErrorOnToaster,
      }),

    get: <Res>(path = '', opts?: ApiRequestOpts): Promise<Res> =>
      httpGet<Res>({
        url: url(path),
        headers: mergeHeaders(opts?.headers),
        showErrorOnToaster: opts?.showErrorOnToaster,
      }),

    put: <Req, Res>(path: string, data?: Req, opts?: ApiRequestOpts) =>
      httpPut<Req, Res>({
        url: url(path),
        data,
        headers: mergeHeaders(opts?.headers),
      }),

    patch: <Req, Res>(path: string, data?: Req, opts?: ApiRequestOpts) =>
      httpPatch<Req, Res>({
        url: url(path),
        data,
        headers: mergeHeaders(opts?.headers),
      }),

    delete: <Req, Res>(path: string, data?: Req, opts?: ApiRequestOpts) =>
      httpDelete<Req, Res>({
        url: url(path),
        data,
        headers: mergeHeaders(opts?.headers),
      }),

    upload: <Res>(path: string, formData: FormData, opts?: ApiRequestOpts): Promise<Res> =>
      httpPost<FormData, Res>({
        url: url(path),
        data: formData,
        headers: mergeHeaders({
          'Content-Type': 'multipart/form-data',
          ...(opts?.headers ?? {}),
        }),
        showErrorOnToaster: opts?.showErrorOnToaster,
        onUploadProgress: opts?.onUploadProgress,
      }),
  }
}
